/**
 * Board Templates Management Screen
 *
 * Allows admins to view, create, edit, delete, and restore board templates
 * for the active organization. The "Sync to Boards" button uses RunJobButton
 * which resolves the job from shared cache and handles all execution logic.
 */

import { useState } from 'react';
import { useScreenState } from '@/hooks/useScreenState';
import { useAlert, Alert } from '@/components/core/Alert';
import { RunJobButton } from '@/components/core/RunJobButton';
import { ScreenType } from '@/types/screen.types';
import { TemplatesTable } from './TemplatesTable';
import { TemplateEditDialog } from './TemplateEditDialog';
import { logger } from '@/utils/logger';
import { useBoardTemplatesQuery } from '@/hooks/queries';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { Action } from '@/business/types/action.types';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import type {
  DeleteBoardTemplateInput,
  DeleteBoardTemplateOutput,
  RestoreBoardTemplateInput,
  RestoreBoardTemplateOutput,
  UpdateBoardTemplateInput,
  UpdateBoardTemplateOutput,
  CreateBoardTemplateInput,
  CreateBoardTemplateOutput,
  GetBoardTemplatesTemplatesItem,
} from '@/types/generated';
import { JobType } from '@/types/enums/JobType';
import { IconName } from '@/icons';
import { Button } from '@/components/ui/button';
import { Icon } from '@/icons';

export default function BoardTemplatesManagement() {
  const alert = useAlert();
  const { activeOrganizationId } = useActiveOrganization();
  const [showDeleted, setShowDeleted] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<GetBoardTemplatesTemplatesItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data: templatesData, isLoading } = useBoardTemplatesQuery({
    orgId: activeOrganizationId,
    includeDeleted: showDeleted || undefined,
  });

  const { execute: executeDelete } = useOrchestrator<
    DeleteBoardTemplateInput,
    DeleteBoardTemplateOutput
  >(Action.DELETE_BOARD_TEMPLATE);

  const { execute: executeRestore } = useOrchestrator<
    RestoreBoardTemplateInput,
    RestoreBoardTemplateOutput
  >(Action.RESTORE_BOARD_TEMPLATE);

  const { execute: executeUpdate } = useOrchestrator<
    UpdateBoardTemplateInput,
    UpdateBoardTemplateOutput
  >(Action.UPDATE_BOARD_TEMPLATE);

  const { execute: executeCreate } = useOrchestrator<
    CreateBoardTemplateInput,
    CreateBoardTemplateOutput
  >(Action.CREATE_BOARD_TEMPLATE);

  const templates = templatesData?.templates ?? [];

  useScreenState({
    metadata: {
      screenType: ScreenType.BOARD_TEMPLATES_MANAGEMENT,
      requiresAuth: true,
      showHeader: true,
      showSidebar: true,
    },
    onLoad: async (_strategy, state) => {
      logger.info('Board Templates Management: Loading');
      state.setSuccess();
    },
    lifecycle: {
      onUnmount: () => {
        logger.info('Board Templates Management unmounted');
      },
    },
  });

  const handleDelete = (tmpl: GetBoardTemplatesTemplatesItem) => {
    alert.show({
      type: 'warning',
      title: `Delete "${tmpl.name}"?`,
      message: `This will soft-delete the template. It can be restored later.`,
      buttons: [
        { label: 'Cancel', variant: 'outline', onClick: () => {} },
        {
          label: 'Delete',
          variant: 'destructive',
          onClick: async () => {
            try {
              await executeDelete({ template_id: tmpl.id });
            } catch {
              // handled by orchestrator
            }
          },
        },
      ],
    });
  };

  const handleUpdateTag = async (
    tmpl: GetBoardTemplatesTemplatesItem,
    tag: string | null
  ) => {
    const currentConfig = (tmpl.config as Record<string, unknown>) || {};
    const newConfig = { ...currentConfig };
    if (tag) {
      newConfig.tag = tag;
    } else {
      delete newConfig.tag;
    }
    try {
      await executeUpdate({
        template_id: tmpl.id,
        config: newConfig,
      });
    } catch {
      // handled by orchestrator
    }
  };

  const handleRestore = (tmpl: GetBoardTemplatesTemplatesItem) => {
    alert.show({
      type: 'info',
      title: `Restore "${tmpl.name}"?`,
      message: `This will restore the template.`,
      buttons: [
        { label: 'Cancel', variant: 'outline', onClick: () => {} },
        {
          label: 'Restore',
          variant: 'default',
          onClick: async () => {
            try {
              await executeRestore({ template_id: tmpl.id });
            } catch {
              // handled by orchestrator
            }
          },
        },
      ],
    });
  };

  const handleEdit = (tmpl: GetBoardTemplatesTemplatesItem) => {
    setEditingTemplate(tmpl);
  };

  const handleEditSave = async (input: UpdateBoardTemplateInput) => {
    try {
      await executeUpdate(input);
      setEditingTemplate(null);
    } catch {
      // handled by orchestrator
    }
  };

  const handleCreateSave = async (
    input: Omit<CreateBoardTemplateInput, 'organization_id'>
  ) => {
    if (!activeOrganizationId) return;
    try {
      await executeCreate({
        ...input,
        organization_id: activeOrganizationId,
      } as CreateBoardTemplateInput);
      setIsCreating(false);
    } catch {
      // handled by orchestrator
    }
  };

  return (
    <div className="space-y-4 p-6">
      {/* Info section */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Manage board templates for your organization.
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="default"
            size="sm"
            onClick={() => setIsCreating(true)}
          >
            <Icon name={IconName.PLUS} size="sm" className="mr-1.5" />
            Create Template
          </Button>

          <RunJobButton
            jobType={JobType.SYNC_BOARD_TEMPLATES}
            label="Sync to Boards"
            icon={IconName.REFRESH}
            variant="outline"
            size="sm"
            title="Propagate template changes to all derived boards"
          />

          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={e => setShowDeleted(e.target.checked)}
              className="rounded border-muted-foreground/30"
            />
            Show deleted
          </label>
          <span className="text-sm text-muted-foreground">
            Showing {templates.length}{' '}
            {templates.length === 1 ? 'template' : 'templates'}
          </span>
        </div>
      </div>

      {/* Templates Table */}
      <TemplatesTable
        templates={templates}
        isLoading={isLoading}
        onDelete={handleDelete}
        onRestore={handleRestore}
        onUpdateTag={handleUpdateTag}
        onEdit={handleEdit}
      />

      {/* Edit Dialog */}
      {editingTemplate && (
        <TemplateEditDialog
          mode="edit"
          template={editingTemplate}
          onSave={handleEditSave}
          onClose={() => setEditingTemplate(null)}
        />
      )}

      {/* Create Dialog */}
      {isCreating && (
        <TemplateEditDialog
          mode="create"
          onCreate={handleCreateSave}
          onClose={() => setIsCreating(false)}
        />
      )}

      {/* Alert Component */}
      <Alert {...alert.props} />
    </div>
  );
}
